/**
 * Accessibility Announcement System
 * 
 * Provides screen reader announcements using ARIA live regions.
 * Designed for seniors and users with visual impairments.
 * 
 * Features:
 * - Polite announcements (wait for current speech to finish)
 * - Assertive announcements (interrupt current speech)
 * - Queue management to prevent overlapping
 * - Clear API for common announcement patterns
 */

// Announcement priority levels
export type AnnouncementPriority = 'polite' | 'assertive';

// Announcement categories for consistent messaging
export type AnnouncementCategory = 
  | 'navigation'
  | 'action'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'reminder'
  | 'game';

interface Announcement {
  message: string;
  priority: AnnouncementPriority;
  category?: AnnouncementCategory;
  timestamp: number;
}

// Queue for managing announcements
let announcementQueue: Announcement[] = [];
let isProcessing = false;

// Create or get the live region container
const getLiveRegion = (priority: AnnouncementPriority): HTMLElement => {
  const id = `aria-live-${priority}`;
  let region = document.getElementById(id);
  
  if (!region) {
    region = document.createElement('div');
    region.id = id;
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', priority);
    region.setAttribute('aria-atomic', 'true');
    // Visually hidden but accessible to screen readers
    region.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;
    document.body.appendChild(region);
  }
  
  return region;
};

// Initialize live regions on module load
const initLiveRegions = () => {
  if (typeof document !== 'undefined') {
    getLiveRegion('polite');
    getLiveRegion('assertive');
  }
};

// Process the announcement queue
const processQueue = async () => {
  if (isProcessing || announcementQueue.length === 0) return;
  
  isProcessing = true;
  
  while (announcementQueue.length > 0) {
    const announcement = announcementQueue.shift()!;
    const region = getLiveRegion(announcement.priority);
    
    // Clear previous content
    region.textContent = '';
    
    // Small delay to ensure screen reader picks up the change
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Set new content
    region.textContent = announcement.message;
    
    // Wait for announcement to be read (rough estimate based on text length)
    const readTime = Math.max(1000, announcement.message.length * 50);
    await new Promise(resolve => setTimeout(resolve, readTime));
  }
  
  isProcessing = false;
};

/**
 * Announce a message to screen readers
 */
export const announce = (
  message: string,
  priority: AnnouncementPriority = 'polite',
  category?: AnnouncementCategory
): void => {
  if (!message.trim()) return;
  
  const announcement: Announcement = {
    message,
    priority,
    category,
    timestamp: Date.now(),
  };
  
  // For assertive announcements, clear the queue first
  if (priority === 'assertive') {
    announcementQueue = announcementQueue.filter(a => a.priority === 'assertive');
    announcementQueue.unshift(announcement);
  } else {
    announcementQueue.push(announcement);
  }
  
  processQueue();
};

/**
 * Announce page navigation
 */
export const announceNavigation = (pageName: string): void => {
  announce(`Navigated to ${pageName}`, 'polite', 'navigation');
};

/**
 * Announce successful action
 */
export const announceSuccess = (message: string): void => {
  announce(`Success: ${message}`, 'polite', 'success');
};

/**
 * Announce error
 */
export const announceError = (message: string): void => {
  announce(`Error: ${message}`, 'assertive', 'error');
};

/**
 * Announce warning
 */
export const announceWarning = (message: string): void => {
  announce(`Warning: ${message}`, 'assertive', 'warning');
};

/**
 * Announce reminder alert
 */
export const announceReminder = (reminderTitle: string): void => {
  announce(`Reminder: Time for ${reminderTitle}`, 'assertive', 'reminder');
};

/**
 * Announce game result
 */
export const announceGameResult = (result: 'correct' | 'incorrect' | 'complete', details?: string): void => {
  const messages = {
    correct: details ? `Correct! ${details}` : 'Correct!',
    incorrect: details ? `Incorrect. ${details}` : 'Incorrect. Try again.',
    complete: details ? `Game complete! ${details}` : 'Congratulations! Game complete!',
  };
  
  announce(messages[result], 'polite', 'game');
};

/**
 * Announce loading state
 */
export const announceLoading = (itemName?: string): void => {
  const message = itemName ? `Loading ${itemName}...` : 'Loading...';
  announce(message, 'polite', 'info');
};

/**
 * Announce loaded state
 */
export const announceLoaded = (itemName?: string): void => {
  const message = itemName ? `${itemName} loaded` : 'Content loaded';
  announce(message, 'polite', 'info');
};

/**
 * Announce form validation
 */
export const announceFormError = (fieldName: string, error: string): void => {
  announce(`${fieldName}: ${error}`, 'assertive', 'error');
};

/**
 * Announce button action
 */
export const announceAction = (actionDescription: string): void => {
  announce(actionDescription, 'polite', 'action');
};

/**
 * Announce list item count
 */
export const announceListCount = (itemName: string, count: number): void => {
  const plural = count === 1 ? '' : 's';
  announce(`${count} ${itemName}${plural}`, 'polite', 'info');
};

/**
 * Clear all pending announcements
 */
export const clearAnnouncements = (): void => {
  announcementQueue = [];
  const politeRegion = document.getElementById('aria-live-polite');
  const assertiveRegion = document.getElementById('aria-live-assertive');
  if (politeRegion) politeRegion.textContent = '';
  if (assertiveRegion) assertiveRegion.textContent = '';
};

/**
 * React hook for announcements
 */
export const useAnnounce = () => {
  return {
    announce,
    announceNavigation,
    announceSuccess,
    announceError,
    announceWarning,
    announceReminder,
    announceGameResult,
    announceLoading,
    announceLoaded,
    announceFormError,
    announceAction,
    announceListCount,
    clearAnnouncements,
  };
};

// Initialize on load
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLiveRegions);
  } else {
    initLiveRegions();
  }
}

// Export types
export type { Announcement };
